import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Activity, Send, MailCheck, MailX, CheckCheck, Eye, MousePointerClick,
  Play, Pause, RefreshCw, Loader2, AlertTriangle,
} from 'lucide-react';
import { formatDateTime } from '../../lib/formatUtils';

const ICONS = {
  job_started: Play,
  job_stopped: Pause,
  message: Send,
  delivered: CheckCheck,
  read: Eye,
  click: MousePointerClick,
};

const LEVEL_STYLES = {
  success: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-emerald-500/30',
  error: 'bg-red-500/10 text-red-600 dark:text-red-400 ring-red-500/30',
  warning: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-amber-500/30',
  info: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 ring-sky-500/30',
};

const channelIcon = (channel) =>
  channel === 'email'
    ? <MailCheck className="h-3 w-3" />
    : <Send className="h-3 w-3" />;

const relativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const sec = Math.round(diff / 1000);
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  if (d < 30) return `${d}d ago`;
  return formatDateTime(iso);
};

export const OutreachTimeline = ({ leadId }) => {
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState({ next_send_at: null, job_state: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchTimeline = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await api.get(`/smb/outreach/${leadId}/timeline`);
      if (res.data.success) {
        setEvents(res.data.data.events || []);
        setMeta({
          next_send_at: res.data.data.next_send_at,
          job_state: res.data.data.job_state,
        });
        setError(null);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load timeline');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [leadId]);

  useEffect(() => { fetchTimeline(false); }, [fetchTimeline]);

  return (
    <Card data-testid="outreach-timeline-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4 text-violet-500" />
            Automation Timeline
          </CardTitle>
          <div className="flex items-center gap-2">
            {meta.job_state && (
              <Badge variant="outline" className="text-[10px] py-0 h-5 uppercase" data-testid="timeline-job-state">
                {meta.job_state}
              </Badge>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={() => fetchTimeline(true)}
              disabled={refreshing}
              data-testid="timeline-refresh-btn"
            >
              {refreshing
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
        {meta.next_send_at && meta.job_state === 'active' && (
          <p className="text-[11px] text-muted-foreground pt-1" data-testid="timeline-next-send">
            Next scheduled send: <span className="font-medium text-foreground">{relativeTime(meta.next_send_at)}</span>
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-xs text-red-500" data-testid="timeline-error">
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        ) : events.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center" data-testid="timeline-empty">
            No automation events yet — once outreach starts you'll see template fires, deliveries, reads and clicks here.
          </p>
        ) : (
          <ul className="relative space-y-3" data-testid="timeline-list">
            {/* vertical line */}
            <span className="absolute left-[15px] top-2 bottom-2 w-px bg-border" aria-hidden />
            {events.map((ev) => {
              const Icon = ICONS[ev.type] || Activity;
              const tone = LEVEL_STYLES[ev.level] || LEVEL_STYLES.info;
              return (
                <li
                  key={ev.id}
                  className="relative flex items-start gap-3 pl-1"
                  data-testid={`timeline-event-${ev.type}`}
                >
                  <span className={`relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-1 ${tone}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-medium leading-tight">
                        {ev.title}
                        {ev.meta?.channel && ev.type === 'message' && (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 align-middle text-muted-foreground">
                            {channelIcon(ev.meta.channel)}
                          </span>
                        )}
                        {ev.meta?.stub && (
                          <Badge variant="outline" className="ml-1.5 text-[9px] py-0 h-4">stub</Badge>
                        )}
                      </p>
                      <span
                        className="text-[10px] text-muted-foreground shrink-0 tabular-nums"
                        title={ev.timestamp}
                      >
                        {relativeTime(ev.timestamp)}
                      </span>
                    </div>
                    {ev.description && (
                      <p className={`text-[11px] mt-0.5 break-words ${ev.level === 'error' ? 'text-red-500' : 'text-muted-foreground'}`}>
                        {ev.description}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default OutreachTimeline;
